'use client';

import {
  WizardCard,
  SectionHeader,
  Field,
} from '@/components/event-wizard/wizard-ui';
import { AdminCard } from '@/components/ui/admin';
import {
  PAYMENT_METHOD_OPTIONS,
  type FinanceFieldErrors,
  type FinanceFormState,
  type PaymentMethodOption,
  type PayoutVerificationState,
} from '@/components/event-wizard/finance-wizard-utils';
import {
  PaymentGatewayVerificationCard,
} from '@/components/event-wizard/PaymentGatewayVerificationCard';

type Props = {
  form: FinanceFormState;
  onChange: (form: FinanceFormState) => void;
  fieldErrors?: FinanceFieldErrors;
  showBankDetails?: boolean;
  paymentMethodOptions?: PaymentMethodOption[];
  eventId?: string | null;
  returnUrl?: string;
  onPayoutVerificationChange?: (state: PayoutVerificationState) => void;
  /** Use AdminCard layout for event workspace pages. */
  variant?: 'wizard' | 'admin';
};

function SectionShell({
  variant,
  title,
  description,
  children,
}: {
  variant: 'wizard' | 'admin';
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  if (variant === 'admin') {
    return (
      <AdminCard title={title} subtitle={description}>
        {children}
      </AdminCard>
    );
  }
  return (
    <WizardCard>
      <div className="ew-panel-body">
        <SectionHeader title={title} description={description} />
        {children}
      </div>
    </WizardCard>
  );
}

export function FinanceWizardPanel({
  form,
  onChange,
  fieldErrors = {},
  showBankDetails = false,
  paymentMethodOptions = PAYMENT_METHOD_OPTIONS,
  eventId,
  returnUrl,
  onPayoutVerificationChange,
  variant = 'wizard',
}: Props) {
  const patch = (partial: Partial<FinanceFormState>) => onChange({ ...form, ...partial });

  const togglePaymentMethod = (method: string) => {
    const exists = form.payment_methods.includes(method);
    patch({
      payment_methods: exists
        ? form.payment_methods.filter((m) => m !== method)
        : [...form.payment_methods, method],
    });
  };

  const methodRowClass =
    variant === 'admin'
      ? 'flex cursor-pointer items-center justify-between rounded-xl border border-nature-border bg-plaster/25 px-4 py-3 transition-colors hover:bg-plaster/40'
      : 'flex cursor-pointer items-center justify-between rounded-xl border border-neutral-100 bg-neutral-50/60 px-4 py-3';

  return (
    <div className="space-y-6">
      <SectionShell
        variant={variant}
        title="Payment methods"
        description="Enable checkout methods for this event."
      >
        <div className="space-y-3">
          {paymentMethodOptions.map((method) => {
            const enabled = form.payment_methods.includes(method.id);
            return (
              <label key={method.id} className={methodRowClass}>
                <div>
                  <span className={`text-sm font-medium ${variant === 'admin' ? 'text-soot' : 'text-dark'}`}>
                    {method.label}
                  </span>
                  <p className={`text-xs ${variant === 'admin' ? 'text-nature-muted' : 'text-slate-500'}`}>
                    Processing fee ~ {method.fee}
                  </p>
                  {method.description ? (
                    <p className={`mt-0.5 text-xs ${variant === 'admin' ? 'text-nature-muted/80' : 'text-slate-400'}`}>
                      {method.description}
                    </p>
                  ) : null}
                </div>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={() => togglePaymentMethod(method.id)}
                  className="h-4 w-4 rounded border-nature-border text-moss focus:ring-moss/30"
                />
              </label>
            );
          })}
          {fieldErrors.payment_methods && <p className="text-xs text-red-600">{fieldErrors.payment_methods}</p>}
        </div>
      </SectionShell>

      {showBankDetails && (
        <PaymentGatewayVerificationCard
          eventId={eventId}
          returnUrl={returnUrl}
          fieldErrors={fieldErrors}
          onVerificationChange={onPayoutVerificationChange}
          variant={variant}
        />
      )}
    </div>
  );
}
